import { z } from 'zod';
import { ACCOUNT_TYPES, APP_ROLES } from '../enums';
import { emailSchema, timestampMsSchema, uuidSchema } from './common';

export const userRoleShape = z.object({
  id: uuidSchema,
  profileId: uuidSchema,
  orgId: uuidSchema.nullable(),
  role: z.enum(APP_ROLES),
});
export type UserRoleShape = z.infer<typeof userRoleShape>;

export const profileShape = z.object({
  id: uuidSchema,
  email: z.string().email(),
  fullName: z.string().min(1).max(120),
  displayName: z.string().nullable(),
  avatarR2Key: z.string().nullable(),
  accountType: z.enum(ACCOUNT_TYPES),
  primaryOrgId: uuidSchema.nullable(),
  createdAt: timestampMsSchema,
  updatedAt: timestampMsSchema,
});
export type ProfileShape = z.infer<typeof profileShape>;

/** Profile + every role the user holds. Used for "who am I" responses and SSR-style hydrate. */
export const profileWithRolesShape = profileShape.extend({
  roles: z.array(userRoleShape),
});
export type ProfileWithRolesShape = z.infer<typeof profileWithRolesShape>;

/** PATCH /me input. Email + accountType are NOT mutable here — those have their own flows. */
export const updateProfileRequest = z.object({
  fullName: z.string().min(1).max(120).optional(),
  displayName: z.string().min(1).max(120).nullable().optional(),
  avatarR2Key: z.string().nullable().optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequest>;

/** Sign-up wraps creating a profile + (for school/instructor) a brand-new org. */
export const signupRequest = z
  .object({
    email: emailSchema,
    password: z.string().min(8).max(128),
    fullName: z.string().min(1).max(120),
    accountType: z.enum(ACCOUNT_TYPES),
    /** Required for school + instructor. For student signups this is omitted. */
    orgName: z.string().min(1).max(120).optional(),
    orgSlug: z.string().optional(),
  })
  .refine(
    (data) =>
      data.accountType === 'student'
        ? data.orgName === undefined && data.orgSlug === undefined
        : Boolean(data.orgName) && Boolean(data.orgSlug),
    {
      message: 'orgName + orgSlug are required for school / instructor signups',
      path: ['orgName'],
    },
  );
export type SignupRequest = z.infer<typeof signupRequest>;
